import React from 'react';
import SEOHead from '../../components/SEOHead';
import EnhancedContactForm from '../../components/forms/EnhancedContactForm';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import Icon from '../../components/AppIcon';

const ContactPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Contact', url: '/contact' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Bootminds - GCC Consulting Services",
    "description": "Get in touch with Bootminds for Global Capability Center consulting services. Schedule a consultation to discuss your GCC requirements.",
    "provider": {
      "@type": "Organization",
      "name": "Bootminds",
      "address": [
        {
          "@type": "PostalAddress",
          "streetAddress": "1000 Paseo Camarillo, Suite 215",
          "addressLocality": "Camarillo",
          "addressRegion": "CA",
          "postalCode": "93010",
          "addressCountry": "US"
        },
        {
          "@type": "PostalAddress",
          "streetAddress": "101-101, F2 Mahagony, Manyata Tech Park",
          "addressLocality": "Nagwara",
          "addressRegion": "Bengaluru",
          "postalCode": "560045",
          "addressCountry": "IN"
        }
      ]
    }
  };

  const handleFormSubmit = async (formData) => {
    console.log('Form submitted:', formData);
    // Here you would typically send the data to your backend
    // For now, we'll just log it
  };

  return (
    <>
      <SEOHead
        title="Contact Bootminds - GCC Consulting Services"
        description="Get in touch with Bootminds for Global Capability Center consulting services. Schedule a consultation to discuss your GCC requirements."
        keywords="contact Bootminds, GCC consulting, Global Capability Center, consultation, business inquiry"
        canonicalUrl="/contact"
        breadcrumbs={breadcrumbs}
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="container-executive">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-body-medium mb-6">
                <Icon name="Mail" size={14} />
                <span>Get in Touch</span>
              </div>
              
              <h1 className="font-headline font-headline-bold text-3xl lg:text-4xl text-text-primary mb-6">
                Let's Discuss Your
                <span className="block text-accent font-accent">GCC Requirements</span>
              </h1>
              
              <p className="text-body-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
                Ready to transform your global operations? Our team of GCC experts is here to help you 
                navigate the journey from strategy to successful implementation.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20">
          <div className="container-executive">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <EnhancedContactForm
                  title="Start Your GCC Journey"
                  description="Tell us about your Global Capability Center requirements and our team will get back to you within 24 hours."
                  onSubmit={handleFormSubmit}
                />
              </div>

              {/* Contact Information */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-8">
                  {/* Office Locations */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="font-headline font-headline-bold text-lg text-text-primary mb-6">
                      Our Offices
                    </h3>
                    
                    <div className="space-y-6">
                      {/* US Office */}
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="MapPin" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-body font-body-medium text-text-primary mb-2">
                            United States
                          </h4>
                          <address className="text-sm text-text-secondary not-italic">
                            1000 Paseo Camarillo, Suite 215<br />
                            Camarillo, CA 93010<br />
                            United States
                          </address>
                        </div>
                      </div>

                      {/* India Office */}
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="MapPin" size={20} className="text-accent" />
                        </div>
                        <div>
                          <h4 className="font-body font-body-medium text-text-primary mb-2">
                            India
                          </h4>
                          <address className="text-sm text-text-secondary not-italic">
                            101-101, F2 Mahagony<br />
                            Manyata Tech Park, Nagwara<br />
                            Bengaluru 560045<br />
                            India
                          </address>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Why Contact Us */}
                  <div className="bg-muted/30 border border-border rounded-xl p-6">
                    <h3 className="font-headline font-headline-bold text-lg text-text-primary mb-4">
                      Why Choose Bootminds?
                    </h3>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">
                          Proven track record with enterprise clients
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">
                          End-to-end GCC setup and management
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">
                          Expert team with deep industry knowledge
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">
                          Transparent pricing and zero-risk engagement
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Response Time */}
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Icon name="Clock" size={20} className="text-primary" />
                      <h4 className="font-body font-body-medium text-text-primary">
                        Response Time
                      </h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      We typically respond to all inquiries within 24 business hours. 
                      For urgent matters, please mention this in your message.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SharedFooter />
      </div>
    </>
  );
};

export default ContactPage;
